import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from "../api";
import type { Dayjs } from "dayjs";
import type { SentEmailResponse } from "./emails";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export type CertificatePaymentState = "pending" | "paid" | "cancelled" | "refunded";
export type CertificateItemState = "pending" | "redeemed" | "cancelled";
export type CertificateItemKind = "voucher" | "merch";

export interface CertificateItemResponse {
  id: number;
  kind: CertificateItemKind;
  product_code: string;
  product_name: string;
  voucher_type_id: number | null;
  merch_kind_id: number | null;
  claim_barcode: string;
  state: CertificateItemState;
  redeemed_voucher_id: string | null;
  redeemed_at: Dayjs | null;
  redeemed_by_user_id: number | null;
}

export interface CertificateResponse {
  id: number;
  eshop_order_id: string;
  eshop_order_number: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string;
  payment_state: CertificatePaymentState;
  payment_method_id: string;
  payment_method_name: string;
  paid_at: Dayjs | null;
  note: string | null;
  redemption_code: string;
  created_at: Dayjs;
  redeemed_at: Dayjs | null;
  pdf_generated_at: Dayjs | null;
  email_sent_at: Dayjs | null;
  manual_intervention: boolean;
  valid_until: Dayjs | null;
  cancelled_at: Dayjs | null;
  cancelled_by_user_id: number | null;
  cancel_reason: string | null;
  items: CertificateItemResponse[];
  /**
   * Set only by mark-paid: the payment was recorded, but letterman will never
   * deliver the certificate's e-mail (a refused or blocked address), so it has
   * to be sent by hand to a corrected one.
   */
  email_warning?: string;
}

export interface CertificateListResponse {
  items: CertificateResponse[];
  next_cursor: string | null;
  total_estimate: number;
}

export interface RedeemItemResponse {
  item: CertificateItemResponse;
  issued_voucher: IssuedVoucherSummary | null;
}

export interface IssuedVoucherSummary {
  id: string;
  barcode: string;
  rides_total: number;
  valid_from: Dayjs;
  valid_until: Dayjs;
}

// --------------------------------------------------------------------------
// Queries
// --------------------------------------------------------------------------

export function useCertificate(id: number | undefined) {
  return useQuery<CertificateResponse>({
    queryKey: ["certificates", id],
    queryFn: () => apiGet(`/certificates/${id}`),
    enabled: id !== undefined,
  });
}

export interface CertificateListFilters {
  payment_state?: string;
  q?: string;
  cursor?: string;
  limit?: number;
  /** Skip the request when false — the list endpoint is manager-only. */
  enabled?: boolean;
}

export function useCertificateList(filters: CertificateListFilters) {
  const params = new URLSearchParams();
  if (filters.payment_state) params.set("payment_state", filters.payment_state);
  if (filters.q) params.set("q", filters.q);
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();

  return useQuery<CertificateListResponse>({
    queryKey: ["certificates", "list", filters],
    queryFn: () => apiGet(`/certificates${qs ? `?${qs}` : ""}`),
    enabled: filters.enabled ?? true,
  });
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export function useLookupCertificate() {
  return useMutation<CertificateResponse, unknown, { code: string }>({
    mutationFn: (payload) => apiPost("/certificates/lookup", payload),
  });
}

export function useRedeemCertificateItem(certId: number) {
  const qc = useQueryClient();
  return useMutation<
    RedeemItemResponse,
    unknown,
    { itemId: number; voucherBarcode?: string }
  >({
    mutationFn: ({ itemId, voucherBarcode }) =>
      apiPost(
        `/certificates/${certId}/items/${itemId}/redeem`,
        voucherBarcode ? { voucher_barcode: voucherBarcode } : {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certificates", certId] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}

// --------------------------------------------------------------------------
// Superadmin mutations
// --------------------------------------------------------------------------

export interface ManualCertItemPayload {
  kind: "voucher" | "merch";
  product_code?: string;
  product_name: string;
  voucher_type_id?: number | null;
  merch_kind_id?: number | null;
}

export interface CreateCertificatePayload {
  eshop_order_number?: string;
  customer_name: string;
  customer_phone?: string | null;
  customer_email: string;
  payment_state: CertificatePaymentState;
  note?: string | null;
  items: ManualCertItemPayload[];
}

export interface UpdateCertificatePayload {
  eshop_order_number?: string;
  customer_name: string;
  customer_phone?: string | null;
  customer_email: string;
  payment_state: CertificatePaymentState;
  note?: string | null;
}

export function useCreateCertificate() {
  const qc = useQueryClient();
  return useMutation<CertificateResponse, unknown, CreateCertificatePayload>({
    mutationFn: (payload) => apiPost("/certificates", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["certificates", "list"] }),
  });
}

export function useUpdateCertificate() {
  const qc = useQueryClient();
  return useMutation<
    CertificateResponse,
    unknown,
    { id: number; payload: UpdateCertificatePayload }
  >({
    mutationFn: ({ id, payload }) => apiPut(`/certificates/${id}`, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["certificates", id] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}

export function useDeleteCertificate() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: number }>({
    mutationFn: ({ id }) => apiDelete(`/certificates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}

export function useUnredeemCertificateItem(certId: number) {
  const qc = useQueryClient();
  return useMutation<
    { item: CertificateItemResponse },
    unknown,
    { itemId: number }
  >({
    mutationFn: ({ itemId }) =>
      apiPost(`/certificates/${certId}/items/${itemId}/unredeem`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certificates", certId] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}

/**
 * Send the delivery e-mail again, at once (letterman accepts it before this
 * resolves), to the customer or to another address.
 */
export function useResendCertificateEmail() {
  const qc = useQueryClient();
  return useMutation<SentEmailResponse, unknown, { id: number; email?: string }>({
    mutationFn: ({ id, email }) =>
      apiPost(`/certificates/${id}/email/resend`, email ? { email } : {}),
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: ["emails"] });
      // A resend to the customer of a certificate that was never e-mailed
      // records email_sent_at, also when it ends in an error such as a
      // blocked address.
      qc.invalidateQueries({ queryKey: ["certificates", id] });
    },
  });
}

/**
 * Mark the certificate paid. kartis sends the delivery e-mail first and fails
 * the whole thing when letterman does not take it, so an error means nothing
 * was recorded and the same click can simply be repeated.
 */
export function useMarkCertificatePaid() {
  const qc = useQueryClient();
  return useMutation<CertificateResponse, unknown, { id: number }>({
    mutationFn: ({ id }) => apiPost(`/certificates/${id}/mark-paid`, {}),
    // Also after an error: the certificate may have been paid meanwhile.
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: ["certificates", id] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
      // Marking paid sends the certificate e-mail: show it on the certificate's e-mail card at once.
      qc.invalidateQueries({ queryKey: ["emails"] });
    },
  });
}

export function useCancelCertificate() {
  const qc = useQueryClient();
  return useMutation<CertificateResponse, unknown, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => apiPost(`/certificates/${id}/cancel`, { reason }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["certificates", id] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}

export interface ExtendCertificateValidityPayload {
  valid_until: string; // ISO datetime
  reason: string;
}

export function useExtendCertificateValidity(certId: number) {
  const qc = useQueryClient();
  return useMutation<CertificateResponse, unknown, ExtendCertificateValidityPayload>({
    mutationFn: (payload) => apiPatch(`/certificates/${certId}/validity`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certificates", certId] });
      qc.invalidateQueries({ queryKey: ["certificates", "list"] });
    },
  });
}
